import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  InputAdornment,
  IconButton,
  Stack,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person as PersonIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { LoginRequest } from '../types';

/**
 * Hero visual for the login left panel — a formal, corporate-style
 * dashboard mockup card (window chrome + abstract bar chart + table
 * rows) over a faint dot-grid backdrop. No illustrated characters —
 * this is the "Stripe/Vercel" style reference: abstract, technical,
 * product-preview-flavored rather than story-illustration-flavored.
 */
const LoginHeroIllustration: React.FC = () => {
  const theme = useTheme();
  const blue = theme.palette.primary.main;
  const blueDark = theme.palette.primary.dark;
  const line = theme.palette.divider;
  const barHeights = [18, 30, 22, 38, 26, 34, 20];

  return (
    <Box sx={{ position: 'relative', width: '100%', maxWidth: 400 }}>
      {/* Faint dot-grid backdrop — plain CSS radial-gradient tile, no
          SVG viewBox to keep in sync with the container's aspect. */}
      <Box
        sx={{
          position: 'absolute',
          inset: -24,
          zIndex: 0,
          backgroundImage: `radial-gradient(${alpha(theme.palette.text.primary, 0.12)} 1.5px, transparent 1.5px)`,
          backgroundSize: '18px 18px',
        }}
      />

      {/* Dashboard mockup card */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          border: `1px solid ${line}`,
          borderRadius: 1,
          backgroundColor: '#FFFFFF',
          boxShadow: '0 12px 32px rgba(15,23,42,0.10)',
          overflow: 'hidden',
        }}
      >
        {/* Window chrome */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${line}`,
          }}
        >
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: theme.palette.divider }} />
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: theme.palette.divider }} />
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: theme.palette.divider }} />
          <Box sx={{ flex: 1 }} />
          <Box sx={{ width: 60, height: 6, borderRadius: 3, bgcolor: theme.palette.divider }} />
        </Box>

        <Box sx={{ p: 2.5 }}>
          {/* KPI row */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
            {[
              { label: '128', sub: 'layar aktif' },
              { label: '+12%', sub: 'CTR minggu ini' },
            ].map((kpi) => (
              <Box key={kpi.sub} sx={{ border: `1px solid ${line}`, borderRadius: 0.5, px: 1.5, py: 1, flex: 1 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: blueDark, lineHeight: 1 }}>
                  {kpi.label}
                </Typography>
                <Typography sx={{ fontSize: 10, color: theme.palette.text.secondary, mt: 0.4 }}>
                  {kpi.sub}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Abstract bar chart */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 48, mb: 2.5 }}>
            {barHeights.map((h, i) => (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  height: h,
                  borderRadius: '2px 2px 0 0',
                  bgcolor: i === 3 ? blue : alpha(blue, 0.25),
                }}
              />
            ))}
          </Box>

          {/* Table rows */}
          {[100, 82, 64].map((w, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.9 }}>
              <Box sx={{ width: 20, height: 20, borderRadius: 0.5, bgcolor: alpha(blue, 0.12), flexShrink: 0 }} />
              <Box sx={{ height: 6, borderRadius: 3, bgcolor: line, width: `${w}%` }} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const ElegantLogin: React.FC = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState<LoginRequest>({
    username: '',
    password: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as any)?.from?.pathname || '/';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) clearError();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await login(formData);
      navigate(from, { replace: true });
    } catch {
      // Handled by AuthContext
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        // Stack vertically on mobile, two columns from md up.
        flexDirection: { xs: 'column', md: 'row' },
      }}
    >
      {/* ─── LEFT: brand / hero panel ────────────────────────────── */}
      <Box
        sx={{
          // Hide entirely below md so the form gets the whole viewport.
          display: { xs: 'none', md: 'flex' },
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          // Light, not a full-bleed saturated blue — blue is now just
          // the illustration/accent color, not the whole backdrop.
          backgroundColor: '#F7F9FC',
          borderRight: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary,
          p: 8,
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        {/* One soft pale-blue wash, decorative, behind content */}
        <Box
          sx={{
            position: 'absolute',
            top: '-15%',
            right: '-15%',
            width: 420,
            height: 420,
            borderRadius: '50%',
            background: alpha(theme.palette.primary.main, 0.06),
            filter: 'blur(80px)',
            pointerEvents: 'none',
          }}
        />

        {/* Top: logo + wordmark */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            component="img"
            src="/logo.webp"
            alt="Glassbox"
            sx={{ height: 40, width: 'auto', objectFit: 'contain' }}
          />
          <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '0.02em' }}>
            Portal
          </Typography>
        </Stack>

        {/* Middle: illustration + tagline */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <LoginHeroIllustration />
          <Typography
            variant="h3"
            sx={{ fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.02em', mb: 2, mt: 5 }}
          >
            Kelola jaringan iklan layar Anda dari satu tempat.
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 460, lineHeight: 1.6 }}
          >
            Platform DOOH untuk pasar Indonesia — pantau perangkat, jadwalkan
            kampanye, dan kelola pendapatan partner-merchant secara real-time.
          </Typography>
        </Box>

        {/* Bottom: legal */}
        <Typography variant="caption" color="text.secondary" sx={{ position: 'relative', zIndex: 1 }}>
          © {new Date().getFullYear()} Glassbox Media · Jakarta, Indonesia
        </Typography>
      </Box>

      {/* ─── RIGHT: form panel ───────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Soft tinted background on mobile (where hero is hidden) so the
          // form still feels framed.
          backgroundColor: { xs: alpha(theme.palette.primary.main, 0.04), md: 'background.paper' },
          px: { xs: 3, sm: 6, md: 10 },
          py: { xs: 8, md: 0 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          {/* Mobile-only logo (hero is hidden < md) */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              justifyContent: 'center',
              mb: 4,
            }}
          >
            <Box component="img" src="/logo.webp" alt="Glassbox" sx={{ height: 56 }} />
          </Box>

          {/* Bordered card frames the form instead of it floating loose
              on the page — matches the Paper treatment used everywhere
              else in the app. */}
          <Box
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: { xs: 3, sm: 5 },
              backgroundColor: '#FFFFFF',
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Masuk
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Gunakan akun admin / partner / merchant Anda untuk mengakses dashboard.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                name="username"
                label="Username"
                autoComplete="username"
                autoFocus
                value={formData.username}
                onChange={handleChange}
                disabled={isSubmitting}
                required
                sx={{ mb: 2.5 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                disabled={isSubmitting}
                required
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        disabled={isSubmitting}
                        aria-label="toggle password visibility"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                <Typography
                  variant="body2"
                  component="a"
                  href="#"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    fontWeight: 500,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Lupa password?
                </Typography>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isSubmitting || !formData.username || !formData.password}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                }}
              >
                {isSubmitting ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={20} color="inherit" />
                    <span>Sedang masuk…</span>
                  </Stack>
                ) : (
                  'Masuk'
                )}
              </Button>
            </Box>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 4 }}
          >
            Bantuan? Hubungi <a href="mailto:support@glassbox.id" style={{ color: 'inherit' }}>support@glassbox.id</a>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ElegantLogin;
